
import { Vue, Component, Prop } from "vue-property-decorator";

import user_routes from "@/api/routes/users";
import program_routes from "@/api/routes/programs";

import CInfiniteScroll from "@/components/InfiniteScroll.vue";

import UserUser from "@/models/user/user";
import ProgramInspector from "@/models/program/inspector";
import { EAdmissionPermission } from "@/models/admission/permission";

interface IData {
  readonly current_page: number;
  readonly next_page: boolean;
  readonly users: UserUser[];
}

@Component({
  name: "CProgramMaterialInspector",
  components: {
    CInfiniteScroll
  }
})
export default class CProgramMaterialInspector extends Vue {
  @Prop({ required: true }) level!: number;
  @Prop({ required: true }) program_id!: string;
  @Prop({ required: true }) program_version_id!: string;

  private preload_infinite: boolean = false;

  public search: string = "";

  private per_page: number = 100;
  private current_page: number = 0;
  private next_page: boolean = false;

  public selected_users: Set<string> = new Set();

  private current_user: IJWTUser | null = this.$store.state.currentUser;

  public users: UserUser[] = [];

  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.watchers.push(
      this.$store.watch(
        state => state.currentUser,
        currentUser => {
          this.current_user = currentUser;
        }
      )
    );

    await this.clearUsers();
  }

  public async clearUsers() {
    this.current_page = 0;
    this.users = [];

    await this.loadUsers();
  }

  public async infiniteHandler() {
    if (this.next_page && !this.preload_infinite) {
      await this.loadUsers(this.current_page + 1);
    }
  }

  private async loadUsers(page: number = 0) {
    this.preload_infinite = true;

    let only_my = true;

    if (this.current_user?.is_admin) {
      only_my = false;
    }

    return this.$api
      .get(user_routes.users, {
        params: {
          page,
          per_page: this.per_page,
          search: this.search,
          paginate: true,
          level: this.level,
          program_id: this.program_id,
          exclude_inspectors: true,
          include_position: true,
          only_my,
          permission_id: EAdmissionPermission.EDUCATION_PROGRAMS
        }
      })
      .then(({ data: res }: { data: IData }) => {
        this.current_page = res.current_page;
        this.next_page = res.next_page;
        this.users.push(...res.users);
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      })
      .finally(() => {
        this.preload_infinite = false;
      });
  }

  public changeCheckedUsers(id: string) {
    if (this.selected_users.has(id)) {
      this.selected_users.delete(id);
    } else {
      this.selected_users.add(id);
    }

    // Set не реактивен во 2-рой версии вью.
    this.selected_users = new Set(this.selected_users);
  }

  public async addUsers() {
    return this.$api
      .post(program_routes.inspectors, {
        inspectors: Array.from(this.selected_users),
        level: this.level,
        program_id: this.program_id,
        program_version_id: this.program_version_id
      })
      .then(async ({ data: res }: { data: ProgramInspector[] }) => {
        this.$emit("input", res);
        this.$emit("close", true);

        await this.changesExist();
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      });
  }

  public selectSupervisor() {
    this.$emit("selectSupervisor", true);
  }

  public selectMentor() {
    this.$emit("selectMentor", true);
  }

  private async changesExist() {
    if (!this.program_version_id) {
      return;
    }

    return this.$api
      .put(program_routes.changes_exist(this.program_version_id!), {})
      .then(() => {
        this.$emit("changesExist", true);
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      });
  }
}
